// Controlador de aplicación: integra lógica, escena 3D, UI, audio y progresión.
// Gestiona el flujo de pantallas y el bucle de turnos (humano + IA).
import { SceneManager } from './SceneManager.js';
import { AudioManager } from './AudioManager.js';
import { InputManager } from './InputManager.js';
import { ProgressionManager } from './ProgressionManager.js';
import { SaveManager } from './SaveManager.js';
import { getConfig, applyUserSettings } from './Config.js';
import { Game } from '../game/Game.js';
import { AIPlayer } from '../game/AIPlayer.js';
import { AIRunner } from './AIRunner.js';
import { DEF_BY_ID } from '../game/TileBag.js';
import { avatarHTML } from '../ui/ProfileUI.js';
import { createTileInstance } from '../game/Tile.js';
import { GameScene } from '../scenes/GameScene.js';
import { NetSync } from '../net/NetSync.js';
import { FriendNet, randomFriendCode } from '../net/FriendNet.js';
import { pwa } from './PWA.js';
import { AuthManager } from './AuthManager.js';
import { RankingService } from './RankingService.js';
import { AsyncService } from '../net/AsyncService.js';
import { FCM_VAPID_KEY } from './fcm-config.js';
import { UIManager } from '../ui/UIManager.js';
import { WordDefsOverlay } from '../ui/WordDefsOverlay.js';
import { EVENTS, GAME_MODE, TILE_TYPE } from '../utils/constants.js';
import { seedFromDateString } from '../utils/helpers.js';
import { normalizeForLookup, letterCount } from '../utils/unicode.js';
import { bus } from './EventBus.js';

export class App {
  constructor(canvas, uiRoot) {
    this.bus = bus;
    this.config = getConfig();
    applyUserSettings(SaveManager.loadSettings() || {});
    this._applyAccessibility(); // aplica los ajustes guardados (contraste, movimiento, texto)

    this.progression = new ProgressionManager(this.bus, this.config);
    this.auth = new AuthManager(); // login obligatorio con Google
    this.ranking = new RankingService(this.auth.app); // ranking global (Firestore)
    this.async = new AsyncService(this.auth.app); // partidas por turnos (Firestore)
    this.messaging = null; // FCM (push "es tu turno"); se carga solo si hay clave VAPID
    if (FCM_VAPID_KEY) this._initMessaging();
    this.audio = new AudioManager(this.bus, this.config);
    this.friendNet = new FriendNet(this.bus); // red de amigos (invitaciones directas)
    this.input = new InputManager(this.bus);

    this.sceneManager = new SceneManager(canvas, {
      quality: this.config.graphics.defaultQuality,
      shadows: this.config.graphics.shadows,
    });
    this.sceneManager.start();

    this.game = new Game({ bus: this.bus, config: this.config });
    this.gameScene = null;

    // Precarga la tipografía de las fichas (Space Grotesk) y, cuando esté lista,
    // regenera las texturas para que las fichas ya dibujadas usen la fuente correcta.
    if (typeof document !== 'undefined' && document.fonts) {
      ['700', '500', '400'].forEach((w) => { try { document.fonts.load(`${w} 40px "Space Grotesk"`); } catch (_) {} });
      document.fonts.ready.then(() => this.gameScene?.refreshTileTextures?.());
    }
    this.currentMode = null;
    this._timerHandle = null;
    this._aiTimeout = null;   // setTimeout del "pensar" de la IA (para poder CANCELARLO al abandonar)
    this._gameSession = 0;    // id de la partida en curso: invalida IAs/timeouts de partidas anteriores
    this.net = null; // NetSync activo en multijugador (null = un solo dispositivo)

    // Ejecuta la búsqueda de la IA en un Web Worker (no bloquea la interfaz).
    this.aiRunner = new AIRunner();

    this.ui = new UIManager({
      root: uiRoot,
      bus: this.bus,
      game: this.game,
      progression: this.progression,
      audio: this.audio,
      config: this.config,
      handlers: this._buildHandlers(),
      friendNet: this.friendNet,
      ranking: this.ranking,
    });
    // Cuando el navegador ofrezca instalar la app, refresca el hub (aparece el botón).
    pwa.onChange = () => this.ui.hub.refresh();

    // Badges "?" con la definición RAE sobre cada palabra válida del tablero.
    this.wordDefs = new WordDefsOverlay({
      root: uiRoot,
      bus: this.bus,
      sceneManager: this.sceneManager,
      modals: this.ui.modals,
      getGame: () => this.game,
      getScene: () => this.gameScene,
    });

    this._bindGlobalEvents();
    this._bindShortcuts();
    this._unlockAudioOnce();
  }

  _buildHandlers() {
    return {
      onTutorial: () => this.startTutorial(),
      onStartGame: (opts) => this.startGame(opts),
      onConfirm: () => this.confirmTurn(),
      onUndo: () => this.commit('undo'),
      onReset: () => this.commit('resetTurn'),
      onPass: () => this._humanPass(),
      onResign: () => this.resignGame(),
      onStartOnline: (net, opts) => this.startOnlineGame(net, opts),
      onDropFromHand: (id, x, y) => this.gameScene?.placeFromHand(id, x, y),
      onQualityChange: (q) => { this.config.graphics.defaultQuality = q; this.sceneManager.setQuality(q); this._persistSettings(); },
      onSettingsChanged: () => this._persistSettings(),
      onDailyReward: () => this._claimDaily(),
      onDailyWord: () => this._dailyWordHTML(),
      onLogout: () => this._logout(),
      onResetAll: () => this._resetEverything(),
      // Partidas por turnos (asíncrono, Firebase):
      onAsyncList: () => this.listAsyncMatches(),
      onAsyncCreate: () => this.createAsyncMatch(),
      onAsyncJoin: (code) => this.joinAsyncMatch(code),
      onAsyncOpen: (match) => this.startAsyncMatch(match),
      asyncTurnCount: () => this.asyncTurnCount(),
      requestNotifyPermission: () => this._requestNotifyPermission(),
      isGuest: () => this._isGuestSession(),
      selfUid: () => this._selfUid(),
    };
  }

  // ---------------------------------------------------------------------------
  // Arranque
  // ---------------------------------------------------------------------------
  async boot() {
    const fill = document.getElementById('loading-bar-fill');
    const tip = document.getElementById('loading-tip');
    const setPct = (p) => { if (fill) fill.style.width = `${Math.min(100, Math.max(0, p))}%`; };

    if (tip) tip.textContent = 'Cargando diccionario de la RAE…';
    setPct(8);
    try {
      const words = await this._loadFullDictionary(setPct);
      if (words && words.length) {
        this.game.validator.addNormalizedWords(words);
        if (tip) tip.textContent = `Diccionario cargado: ${this.game.validator.size.toLocaleString('es')} palabras`;
      }
    } catch (err) {
      console.warn('[App] No se pudo cargar el diccionario completo; se usa el semilla.', err);
      if (tip) tip.textContent = 'Diccionario básico (sin conexión al completo)';
    }
    setPct(100);

    // Login OBLIGATORIO con Google: recupera la sesión guardada (si la hay) antes de decidir
    // si mostramos el juego o la pantalla de acceso.
    if (tip) tip.textContent = 'Comprobando sesión…';
    let user = null;
    try { user = await this.auth.init(); } catch (err) { console.warn('[Auth] init falló', err); }
    this.auth.onChange((u) => this._onAuthChanged(u));

    setTimeout(() => {
      document.getElementById('loading-screen')?.classList.add('gone');
      if (user) this._enterApp(user);                 // sesión de Google
      else if (this._isGuestPersisted()) this._enterAsGuest(); // invitado local (sin registro)
      else this._showLogin();
    }, 350);
  }

  _isGuestPersisted() {
    try { return localStorage.getItem('wb:guest') === '1'; } catch { return false; }
  }

  // ---------------------------------------------------------------------------
  // Login con Google (obligatorio)
  // ---------------------------------------------------------------------------
  /** Reacciona a cambios de sesión de Google: entrar al iniciar sesión, salir al cerrarla.
   *  En modo INVITADO (local, sin Firebase) se ignoran los eventos null para no expulsar. */
  _onAuthChanged(user) {
    if (user) {
      if (!this._entered) this._enterApp(user);
    } else if (this._entered && !this._isGuestPersisted()) {
      // Cierre de sesión de Google durante el juego: vuelve a la pantalla de acceso.
      this._entered = false;
      this.returnToHub?.();
      this._showLogin();
    }
  }

  /** Entra al juego con una sesión de Google: aplica el perfil y muestra el hub. */
  _enterApp(user) {
    if (this._entered) return;
    this._applyGoogleProfile(user);
    this._finishEnter();
  }

  /** Entra como INVITADO local (sin registro, SIN Firebase): perfil marcado como anónimo. */
  _enterAsGuest() {
    if (this._entered) return;
    try { localStorage.setItem('wb:guest', '1'); } catch (_) {}
    const p = this.progression.profile;
    p.google = { uid: null, email: null, anon: true }; // anónimo sin uid → no sube al ranking
    if (!p.nameCustom) p.name = 'Invitado';
    p.onboarded = true;
    this.progression.save();
    this.ui.hub?.refresh?.();
    this._finishEnter();
  }

  /** Parte común al entrar (Google o invitado): oculta login, muestra hub, engancha red y enlaces. */
  _finishEnter() {
    this._entered = true;
    this._hideLogin();
    this.ui.showHub();
    this._startFriendNet();
    const params = new URLSearchParams(location.search);
    // Añadir amigo si se abrió con un enlace de amigo (?friend=CÓDIGO&name=&avatar=).
    const friend = params.get('friend');
    if (friend) {
      this.ui.friends.addFriendData({ code: friend.toUpperCase(), name: params.get('name'), avatar: params.get('avatar') });
      this.ui.toast(`${params.get('name') || 'Un amigo'} añadido a tus amigos`, { icon: '🧑‍🤝‍🧑', duration: 2600 });
    }
    // Auto-unión a una sala si se abrió con enlace/QR (?join=CÓDIGO).
    const join = params.get('join');
    if (join) this.ui.openFriends(join.toUpperCase());
    // Invitación a partida por turnos (?reta=CÓDIGO): abre "Reta a un jugador" con el código.
    const reta = params.get('reta');
    if (reta) this.ui.openAsyncMatches(reta.toUpperCase());
    // Aviso "es tu turno": comprueba partidas donde te toca (sin coste, al entrar).
    setTimeout(() => this._checkAsyncTurns(), 1200);
  }

  /** Cierra sesión (Google o invitado) y vuelve a la pantalla de acceso. */
  _logout() {
    try { localStorage.removeItem('wb:guest'); } catch (_) {}
    this._entered = false;
    this.returnToHub?.();
    this._showLogin();
    this.auth.signOutUser().catch(() => {}); // limpia la sesión de Google si la había
  }

  /** Sincroniza el perfil con la sesión (Google o INVITADO anónimo).
   *  El nombre se toma de Google en CADA login MIENTRAS el jugador no lo haya cambiado a
   *  mano (flag `nameCustom`). El invitado usa "Invitado" por defecto. */
  _applyGoogleProfile(user) {
    if (!user) return;
    const p = this.progression.profile;
    const anon = !!user.isAnonymous;
    p.google = { uid: user.uid, email: user.email || null, anon };
    const fallback = anon ? 'Invitado' : 'Jugador';
    const displayName = (user.displayName || (user.email || '').split('@')[0] || fallback).slice(0, 16);
    if (!p.nameCustom) p.name = displayName;
    if (!p.avatarCustom && user.photoURL) {
      p.avatar = 'custom';
      // Provisional: la URL de Google. En cuanto haya red se DESCARGA y se guarda como data URL
      // (imagen embebida) para que funcione también SIN conexión (evita el cuadro de imagen rota).
      if (!String(p.avatarImage || '').startsWith('data:')) p.avatarImage = user.photoURL;
      this._cacheAvatarImage(user.photoURL);
    }
    p.onboarded = true;
    this.progression.save();
    this.ui.hub?.refresh?.();
  }

  /** Descarga la foto de perfil (con internet) y la guarda como data URL para uso OFFLINE. */
  async _cacheAvatarImage(url) {
    if (!url || String(url).startsWith('data:')) return;
    try {
      const res = await fetch(url, { mode: 'cors', cache: 'force-cache' });
      if (!res.ok) return;
      const blob = await res.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
      const p = this.progression.profile;
      // Solo si el jugador no ha puesto una imagen propia mientras tanto.
      if (!p.avatarCustom && typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
        p.avatar = 'custom';
        p.avatarImage = dataUrl; // ahora persiste embebida → se ve sin conexión
        p.avatarImageSrc = url;  // recuerda el origen para no re-descargar en balde
        this.progression.save();
        this.ui.hub?.refresh?.();
      }
    } catch (_) { /* CORS o sin red: se queda la URL; el fallback evita el cuadro roto */ }
  }

  /** Muestra la pantalla de acceso y engancha el botón de Google (una sola vez). */
  _showLogin() {
    const screen = document.getElementById('login-screen');
    if (!screen) return;
    screen.classList.remove('hidden');
    const errEl = document.getElementById('login-error');
    if (errEl) errEl.textContent = '';
    if (this._loginWired) return;
    this._loginWired = true;
    const btn = document.getElementById('google-signin');
    btn?.addEventListener('click', async () => {
      btn.disabled = true;
      btn.classList.add('is-loading');
      if (errEl) errEl.textContent = '';
      try {
        await this.auth.signIn();
      } catch (err) {
        console.warn('[Auth] signIn falló', err);
        if (errEl) errEl.textContent = 'No se pudo iniciar sesión. Inténtalo de nuevo.';
      } finally {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    });
    // Entrar como INVITADO local (sin registro, SIN Firebase): entra directo, siempre funciona.
    const guest = document.getElementById('guest-signin');
    guest?.addEventListener('click', () => {
      if (errEl) errEl.textContent = '';
      this._enterAsGuest();
    });
  }

  _hideLogin() {
    document.getElementById('login-screen')?.classList.add('hidden');
  }

  /** Reinicio COMPLETO: borra el ranking global propio y TODO el almacenamiento local,
   *  y recarga la app (queda como recién instalada; la sesión de Google se conserva). */
  async _resetEverything() {
    const uid = this.progression.profile.google?.uid;
    if (uid) { try { await this.ranking.remove(uid); } catch (_) {} }
    SaveManager.resetAll();
    try { SaveManager.clearGame(); } catch (_) {}
    location.reload();
  }

  /** Avatar del jugador para el ranking: URL de la foto si la hay, si no el emoji. */
  _selfRankAvatar() {
    const p = this.progression.profile;
    if (p.avatarImage) return p.avatarImage; // foto de Google o imagen subida
    if (p.avatar && p.avatar !== 'default' && p.avatar !== 'custom') return p.avatar;
    return '🙂';
  }

  /** Descarga el diccionario RAE (asset estático) mostrando progreso real. */
  async _loadFullDictionary(setPct) {
    const base = (import.meta.env && import.meta.env.BASE_URL) || './';
    const res = await fetch(`${base}dictionary-es.json`, { cache: 'force-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const total = Number(res.headers.get('content-length')) || 0;
    if (res.body && total > 0) {
      // Lectura por streaming para reflejar el progreso de descarga.
      const reader = res.body.getReader();
      const chunks = [];
      let received = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        setPct(8 + (received / total) * 82); // 8%..90%
      }
      const blob = new Blob(chunks);
      const text = await blob.text();
      setPct(94);
      return JSON.parse(text).words;
    }
    // Sin content-length: descarga directa.
    const data = await res.json();
    setPct(94);
    return data.words;
  }

  _unlockAudioOnce() {
    const unlock = () => {
      this.audio.unlock();
      this._tryFullscreen();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
  }

  /** Pantalla completa en MÓVIL (oculta la barra de estado/reloj). Solo si no es ya PWA instalada. */
  _tryFullscreen() {
    try {
      const isMobile = window.matchMedia?.('(pointer: coarse)').matches;
      const installed = window.matchMedia?.('(display-mode: fullscreen)').matches
        || window.matchMedia?.('(display-mode: standalone)').matches
        || window.navigator.standalone;
      if (!isMobile || installed || document.fullscreenElement) return;
      const el = document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen;
      req?.call(el, { navigationUI: 'hide' });
    } catch { /* el navegador puede bloquearlo; sin problema */ }
  }

  // ---------------------------------------------------------------------------
  // Ciclo de partida
  // ---------------------------------------------------------------------------
  startGame({ mode = GAME_MODE.CLASSIC, aiLevel = 'normal', numAI = 1, timerSeconds = 0 }) {
    this._resetGameRuntime(); // invalida IA/timeouts de cualquier partida anterior
    this.currentMode = mode;
    this._disposeGameScene();
    this._modeState = null;

    // Configuración específica del modo.
    let seed = (Math.floor(Date.now?.() ?? 1) % 1e9) || 12345;
    // Cuenta atrás por turno en TODOS los modos (por defecto 60 s).
    const defSecs = this.config.timer.default || 60;
    let timer = timerSeconds || defSecs;

    if (mode === GAME_MODE.DAILY) {
      seed = seedFromDateString(this._todayStr()); // mismas fichas para todos hoy
      numAI = 0;                                    // reto en solitario
      this._modeState = { daily: true, targetWord: this._dailyWord() };
    } else if (mode === GAME_MODE.SURVIVAL) {
      numAI = 0;
      this._modeState = { survival: true, round: 1, minLen: 3, timePerRound: 45 };
      timer = this._modeState.timePerRound;
    } else if (mode === GAME_MODE.TIMED) {
      timer = 25; // Contrarreloj: 25 s máximo por turno (como Clásico pero más rápido).
    } else if (mode === GAME_MODE.CLASSIC) {
      timer = 30; // Clásico: 30 s por turno.
    }

    // TODOS los modos usan temporizador de turno (máximo 60 s).
    const maxSecs = this.config.timer.maxSeconds || 60;
    if (mode === GAME_MODE.SURVIVAL) {
      this._modeState.timePerRound = Math.min(this._modeState.timePerRound, maxSecs);
      timer = this._modeState.timePerRound;
    } else {
      timer = Math.min(timer || defSecs, maxSecs); // siempre con cuenta atrás
    }

    this.game.selfPlayerId = null; // partida local: el humano es 'p0'
    this.game.selfIsHost = null;
    const prof = this.progression.profile;

    // ONBOARDING PROGRESIVO: el jugador nuevo empieza con SOLO LETRAS; al ir jugando
    // se desbloquean sílabas y, más tarde, el resto (comodines, etc.). No aplica al
    // multijugador (ambos deben tener la misma bolsa) ni al Puzzle diario (compartido).
    let allowedTypes = null;
    const onboardingApplies = mode !== GAME_MODE.ONLINE && mode !== GAME_MODE.DAILY;
    if (onboardingApplies) {
      const gp = prof.stats?.gamesPlayed || 0;
      const level = this._onboardingLevel(gp);
      allowedTypes = this._allowedTypesForLevel(level);
      const seen = prof.onboardLevelSeen || 1;
      if (level > seen) {
        prof.onboardLevelSeen = level;
        this.progression.save?.();
        this._pendingUnlock = level; // se anuncia cuando arranca la partida
      } else {
        this._pendingUnlock = 0;
      }
    } else {
      this._pendingUnlock = 0;
    }

    this.game.newGame({
      mode, aiLevel, numAI, seed, timerSeconds: timer, allowedTypes,
      playerName: prof.name,
      playerAvatar: prof.avatar && prof.avatar !== 'default' ? prof.avatar : '🙂',
      playerAvatarImage: prof.avatarImage || null,
    });
    this._enterGameScene();
    // En Clásico y Contrarreloj contra la IA: simula el emparejamiento ("Buscando rivales…")
    // 8–15 s sobre el tablero antes de arrancar el primer turno (el temporizador NO corre aún).
    if ((mode === GAME_MODE.CLASSIC || mode === GAME_MODE.TIMED) && numAI > 0) {
      const session = this._gameSession;
      this._showSearching(session, () => {
        if (!this._sessionAlive(session)) return;
        this._announceObjective(mode);
        this._maybeRunAI();
        this._announceUnlock();
      });
    } else {
      this._announceObjective(mode);
      this._maybeRunAI(); // arranca IA o el temporizador del humano según corresponda
      this._announceUnlock();
    }
  }

  /** Nivel de complejidad desbloqueado según partidas jugadas.
   *  Nivel 1 = partidas 0–3 (solo letras); Nivel 2 = 4–7 (+sílabas); Nivel 3 = 8+ (todo). */
  _onboardingLevel(gamesPlayed) {
    if (gamesPlayed >= 8) return 3; // todo (comodines, etc.)
    if (gamesPlayed >= 4) return 2; // letras + sílabas
    return 1;                        // solo letras
  }

  /** Conjunto de tipos de ficha permitidos para un nivel (null = sin filtro, todo). */
  _allowedTypesForLevel(level) {
    if (level >= 3) return null;
    if (level === 2) return new Set(['letter', 'syllable']);
    return new Set(['letter']);
  }

  /** Avisa (una vez) al cruzar a un nuevo nivel de complejidad. */
  _announceUnlock() {
    const level = this._pendingUnlock;
    this._pendingUnlock = 0;
    if (!level) return;
    // Pequeño retraso para que no choque con el aviso de objetivo/turno.
    setTimeout(() => {
      if (level === 2) {
        this.ui.toast('¡Nuevo! Aparecen SÍLABAS · combínalas para formar palabras más rápido.', { duration: 4200 });
      } else if (level >= 3) {
        this.ui.toast('¡Nuevo! Ya tienes TODAS las fichas · comodines y más profundidad.', { duration: 4200 });
      }
    }, 1400);
  }

  /** Crea la escena 3D, cablea el commit y muestra la partida. */
  _enterGameScene() {
    this.gameScene = new GameScene(this.sceneManager, this.game, this.bus);
    this.gameScene.getHandTopY = () => this.ui.handPanel.topY();
    this.gameScene.commit = (method, ...args) => this.commit(method, ...args);
    this.gameScene.sync();
    this.ui.showGame();
    this.sceneManager.setWatermarkVisible(false); // sin logo de fondo en el tablero
    // En el tutorial ("Cómo se juega") NO se muestra el botón de Pausa.
    const isTut = this.currentMode === GAME_MODE.TUTORIAL;
    this.ui.hud.el.classList.toggle('is-tutorial', isTut);
    // Reserva espacio arriba para la tarjeta guía (que las palabras no queden ocultas).
    this.sceneManager.tutorialMode = isTut;
  }

  /** Ejecuta una acción del juego, enrutándola por red si hay partida multijugador. */
  commit(method, ...args) {
    if (this.net) return this.net.commit(method, ...args);
    return this.game[method](...args);
  }

  // ---------------------------------------------------------------------------
  // Red de AMIGOS (código estable + invitaciones directas)
  // ---------------------------------------------------------------------------
  _startFriendNet() {
    const p = this.progression.profile;
    if (!p.friendCode) { p.friendCode = randomFriendCode(); this.progression.save(); }
    this.friendNet.start(p.friendCode, this._friendMe()).catch(() => {});
  }

  _friendMe() {
    const p = this.progression.profile;
    const av = p.avatar && p.avatar !== 'default' && p.avatar !== 'custom' ? p.avatar : '🙂';
    return { name: p.name, avatar: av, code: p.friendCode };
  }

  _onFriendInvite(room, from) {
    // Guarda a quien te invita como amigo (trae nombre/avatar/código).
    if (from?.code) this.ui.friends.addFriendData(from);
    // Si estás en plena partida, solo avisa.
    if (this.gameScene && this.game.state && !this.game.state.finished) {
      this.ui.toast(`${from?.name || 'Un amigo'} te invita a jugar`, { icon: '📨', duration: 3200 });
      return;
    }
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'reward' });
    const inviter = from?.name || 'Un amigo';
    const modal = this.ui.modals.open({
      title: 'TE INVITAN A JUGAR',
      className: 'mode-modal modes-modal invite-modal',
      dismissible: false,
      bodyHTML: `<p class="invite-text"><b>${inviter}</b> te ha invitado a una partida.</p>`,
    });
    // Rechazar: círculo con X arriba a la derecha. Avisa a quien invitó.
    const rejectBtn = document.createElement('button');
    rejectBtn.className = 'modal-close';
    rejectBtn.setAttribute('aria-label', 'Rechazar');
    rejectBtn.innerHTML = '✕';
    rejectBtn.addEventListener('click', () => {
      this.ui.modals.close();
      if (from?.code) { try { this.friendNet.reject(from.code); } catch { /* noop */ } }
    });
    modal.appendChild(rejectBtn);
    // Unirme: círculo con ▶ abajo a la derecha (como el resto de ventanas).
    const joinBtn = document.createElement('button');
    joinBtn.className = 'lobby-start';
    joinBtn.setAttribute('aria-label', 'Unirme');
    joinBtn.innerHTML = '▶';
    joinBtn.addEventListener('click', () => { this.ui.modals.close(); this.ui.openFriends(room); });
    modal.appendChild(joinBtn);
  }

  // ---------------------------------------------------------------------------
  // Multijugador con amigos (host-autoritario)
  // ---------------------------------------------------------------------------
  startOnlineGame(net, { isHost }) {
    this._resetGameRuntime(); // invalida IA/timeouts de cualquier partida anterior
    this.currentMode = GAME_MODE.ONLINE;
    this._modeState = null;
    this._disposeGameScene();
    this._lastActiveIdx = -1;
    this._lastHistLen = 0;
    this.game.selfPlayerId = net.selfId;
    this.game.selfIsHost = isHost; // rol en red (red de seguridad si el id no casara)

    const hooks = {
      onState: () => this._onRemoteState(),
      onEnd: (summary) => this._onNetEnd(summary),
    };

    if (isHost) {
      const roster = net.roster.map((p) => ({ id: p.id, name: p.name, avatar: p.avatar || (p.host ? '👑' : '🙂') }));
      const seed = Math.floor(Math.random() * 1e9);
      const timerSecs = 45; // 1v1 con amigo: 45 s por turno
      this.game.newNetworkGame({ players: roster, seed, timerSeconds: timerSecs });
      this.net = new NetSync({ game: this.game, net, bus: this.bus, isHost: true, selfPlayerId: net.selfId, hooks });
      this._enterGameScene();
      this._lastActiveIdx = this.game.state.activePlayerIndex;
      this._lastHistLen = 0;
      this.net.pushState(); // estado inicial a los invitados
      this._announceTurnSoon();
      this._updateOnlineTimer();
    } else {
      // Invitado: crea NetSync y espera el primer estado del host.
      this.net = new NetSync({ game: this.game, net, bus: this.bus, isHost: false, selfPlayerId: net.selfId, hooks });
      this._pendingGuestEnter = true;
      this.ui.toast('Uniéndote a la partida…', { icon: '⏳' });
    }
  }

  // ---------------------------------------------------------------------------
  // Modo ASÍNCRONO (1v1 por turnos vía Firebase; sin estar ambos conectados)
  // ---------------------------------------------------------------------------
  _selfUid() { return this.progression.profile.google?.uid || null; }
  _isGuestSession() { return !!this.progression.profile.google?.anon; }
  _asyncSelfInfo() {
    const p = this.progression.profile;
    return { uid: p.google?.uid, name: p.name, avatar: this._selfRankAvatar() };
  }

  /** Lista TUS partidas por turnos (Firestore). */
  async listAsyncMatches() {
    const uid = this._selfUid();
    if (!uid) return [];
    try { return await this.async.listMine(uid); } catch (e) { console.warn('[Async] list', e); return []; }
  }

  /** Nº de partidas donde te TOCA jugar (para el badge del hub). */
  asyncTurnCount() { return this._asyncTurnCount || 0; }

  /** Comprueba (al entrar/volver) si tienes turnos pendientes en "Reta a un jugador". Sin coste
   *  (no push): avisa con un toast y, si hay permiso, una notificación local; pone el badge. */
  async _checkAsyncTurns() {
    const g = this.progression.profile.google;
    if (!g?.uid || g.anon) { this._asyncTurnCount = 0; return; }
    try {
      const matches = await this.async.listMine(g.uid);
      const mine = matches.filter((m) => m.status === 'active' && m.turnUid === g.uid);
      const prev = this._asyncTurnCount || 0;
      this._asyncTurnCount = mine.length;
      this.ui.hub?.refresh?.();
      if (mine.length > 0 && !this._asyncTurnNotified) {
        this._asyncTurnNotified = true; // solo una vez por sesión
        const msg = mine.length === 1 ? 'Tienes 1 partida esperando tu turno.' : `Tienes ${mine.length} partidas esperando tu turno.`;
        this.ui.toast(`${msg} (Reta a un jugador)`, { icon: '🔔', duration: 4400 });
        this._localNotifyTurn(mine.length);
      }
    } catch (_) { this._asyncTurnCount = 0; }
  }

  /** Pide permiso de notificaciones (gesto del usuario). Si se concede, registra el token FCM
   *  (si está configurado). Devuelve el estado del permiso. */
  async _requestNotifyPermission() {
    try {
      if (!('Notification' in window)) return 'unsupported';
      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();
      if (perm === 'granted') this._registerPushToken();
      return perm;
    } catch (_) { return 'error'; }
  }

  /** Carga FCM (import dinámico) solo si hay clave VAPID configurada. */
  async _initMessaging() {
    try {
      const { Messaging } = await import('./Messaging.js');
      const m = new Messaging(this.auth.app, this.bus);
      if (await m.init()) this.messaging = m;
    } catch (e) { console.warn('[FCM] initMessaging', e); }
  }

  /** Registra el token FCM del dispositivo en Firestore (para el push "es tu turno").
   *  Solo hace algo si FCM está configurado (clave VAPID + soporte); si no, no-op. */
  async _registerPushToken() {
    try {
      if (!this.messaging) return;
      const token = await this.messaging.registerToken();
      const uid = this._selfUid();
      if (token && uid) await this.async.saveFcmToken(uid, token);
    } catch (e) { console.warn('[FCM] token', e); }
  }

  /** Notificación LOCAL (app abierta / con permiso) de que te toca jugar. */
  _localNotifyTurn(n) {
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        const body = n === 1 ? 'Te toca jugar en una partida.' : `Te toca jugar en ${n} partidas.`;
        new Notification('PALABRIKS · ¡Es tu turno!', { body, icon: 'icons/icon-192.png', tag: 'palabriks-turn' });
      }
    } catch (_) {}
  }

  /** Crea una partida por turnos VACÍA. Devuelve {id, code} para invitar. */
  async createAsyncMatch() {
    return this.async.createMatch(this._asyncSelfInfo());
  }

  /** Se une a una partida por código: inicializa el estado y juega el primer turno. */
  async joinAsyncMatch(code) {
    const me = this._asyncSelfInfo();
    const match = await this.async.findOpenByCode(code, me.uid);
    if (!match) return { error: 'No se encontró una partida abierta con ese código.' };
    const players = [...match.players, { uid: me.uid, name: me.name, avatar: me.avatar || '🙂' }];
    const roster = players.map((p, i) => ({ id: `p${i}`, name: p.name, avatar: p.avatar }));
    const seed = Math.floor(Math.random() * 1e9);
    this.game.newNetworkGame({ players: roster, seed, timerSeconds: 0 });
    const state = this.game.serialize();
    await this.async.updateMatch(match.id, {
      playerUids: [...match.playerUids, me.uid], players, state, turnUid: me.uid, status: 'active',
    });
    const full = { ...match, playerUids: [...match.playerUids, me.uid], players, state, turnUid: me.uid, status: 'active' };
    this.startAsyncMatch(full);
    return { ok: true };
  }

  /** Carga una partida por turnos y (si es tu turno) te deja jugar. */
  startAsyncMatch(match) {
    if (!match?.state) return;
    this._resetGameRuntime();
    this.currentMode = GAME_MODE.ASYNC;
    this._modeState = null;
    this._disposeGameScene();
    this.net = null;
    this._asyncMatch = match;
    const myUid = this._selfUid();
    const mySlot = (match.players || []).findIndex((p) => p.uid === myUid);
    this.game.selfPlayerId = `p${mySlot < 0 ? 0 : mySlot}`;
    this.game.selfIsHost = null;
    this.game.load(match.state);
    this._enterGameScene();
    this._lastActiveIdx = this.game.state.activePlayerIndex;
    this._lastHistLen = this.game.state.history?.length || 0;
    this._announceTurnSoon();
  }

  /** Al terminar TU turno (asíncrono): sube el estado, pasa el turno al rival y sales al hub. */
  async _asyncFinishTurn() {
    const match = this._asyncMatch;
    if (!match) return;
    const state = this.game.serialize();
    const oppUid = (match.playerUids || []).find((u) => u !== this._selfUid()) || null;
    this._asyncMatch = null;
    this.returnToHub();
    this.ui.toast('Turno enviado. Le toca a tu rival.', { icon: '📤', duration: 3000 });
    try { await this.async.updateMatch(match.id, { state, turnUid: oppUid }); }
    catch (e) { console.warn('[Async] no se pudo guardar el turno', e); this.ui.toast('No se pudo guardar el turno (sin conexión).', { icon: '⚠️' }); }
  }

  /** Sube el estado FINAL de una partida asíncrona terminada. */
  async _asyncSaveFinished() {
    const match = this._asyncMatch;
    if (!match) return;
    const winnerId = this.game.state?.winnerId;
    let winnerUid = null;
    if (winnerId) { const i = parseInt(String(winnerId).slice(1), 10); winnerUid = match.players?.[i]?.uid || null; }
    const state = this.game.serialize();
    this._asyncMatch = null;
    try { await this.async.updateMatch(match.id, { state, status: 'finished', turnUid: null, winnerUid }); }
    catch (e) { console.warn('[Async] no se pudo guardar el fin', e); }
  }

  /** Anuncia el turno de forma fiable en el arranque de una partida en red.
   *  El primer TURN_START salta cuando el HUD todavía está oculto (aún no se ha
   *  mostrado la escena de juego) y el anuncio se descarta; por eso lo relanzamos
   *  cuando el HUD ya está visible y pintado, para que al ANFITRIÓN también le
   *  aparezca su "¡TU TURNO!" al empezar (no solo al invitado). */
  _announceTurnSoon() {
    // Reintenta hasta que el HUD esté visible (evita que el anuncio se descarte
    // por dispararse antes de mostrarse la escena). Varios intentos escalonados
    // cubren dispositivos lentos donde el primer frame aún no ha pintado el HUD.
    const hud = this.hud();
    let tries = 0;
    const tryShow = () => {
      if (!hud.el.classList.contains('hidden') && this.game.state) { hud.announceTurn(); return; }
      if (tries++ < 12) requestAnimationFrame(tryShow); // ~200 ms de margen
    };
    tryShow();
  }

  _onRemoteState() {
    if (this._pendingGuestEnter) {
      this._pendingGuestEnter = false;
      this._enterGameScene();
      this._lastActiveIdx = this.game.state.activePlayerIndex;
      this._lastHistLen = this.game.state.history.length;
      this._announceTurnSoon();
      this._updateOnlineTimer(); // arranca la cuenta atrás en el invitado
    }
    // Si el jugador pidió ordenar (doble toque), MANTÉN el orden cuando el rival juega
    // una palabra nueva (en red no salta TURN_START local). No en cada estado, para no
    // mover las fichas mientras el rival solo arrastra.
    const hist = this.game.state.history || [];
    const newPlay = hist.length > this._lastHistLen;
    if (newPlay && this.gameScene?._userArranged && this.game.board) {
      this.gameScene._reorganizeWords(this.game.board);
    }
    this.gameScene?.sync();

    // Toast de jugada nueva (diff del historial) + registro de MIS palabras.
    if (newPlay) {
      const last = hist[hist.length - 1];
      const player = this.game.players.find((p) => p.id === last.playerId);
      const words = (last.newWords || []).join(', ').toUpperCase();
      const icon = player ? avatarHTML(player) : '🎮';
      if (words) this.ui.toast(`${player?.name || 'Alguien'} jugó ${words}`, { icon, duration: 1800 });
      // Si la jugada es MÍA (invitado), registra mis palabras en el perfil.
      if (last.playerId === this.game.selfPlayerId) this._recordSelfWords(last.newWords);
      this._lastHistLen = hist.length;
    }
    // Cambio de turno -> anuncio + reinicio de la cuenta atrás.
    const idx = this.game.state.activePlayerIndex;
    if (idx !== this._lastActiveIdx) {
      this._lastActiveIdx = idx;
      this.hud().announceTurn();
      this._updateOnlineTimer();
    }
  }

  /** Invitado: el host anuncia el fin de la partida. */
  _onNetEnd(summary) {
    this._showResults(summary);
  }

  _announceObjective(mode) {
    if (mode === GAME_MODE.DAILY) {
      this.ui.toast(`Puzzle diario · Palabra del día: ${this._modeState.targetWord.toUpperCase()}`, { icon: '📅', duration: 3200 });
    } else if (mode === GAME_MODE.SURVIVAL) {
      this.ui.toast('Supervivencia · Ronda 1: crea una palabra de 3+ letras', { icon: '💀', duration: 3000 });
    } else if (mode === GAME_MODE.TIMED) {
      this.ui.toast('¡Contrareloj! Cada turno tiene tiempo limitado.', { icon: '⏱️', duration: 2600 });
    }
  }

  startTutorial() {
    this._resetGameRuntime();
    this.currentMode = GAME_MODE.TUTORIAL;
    this.net = null;
    this._disposeGameScene();
    this.game.selfPlayerId = null;
    this.game.selfIsHost = null;
    this.game.newGame({ mode: GAME_MODE.TUTORIAL, numAI: 1, aiLevel: 'easy', seed: 2024 });
    // Mano fija didáctica: CA, SA, S + un sufijo y letras útiles.
    this._setTutorialRack();
    this._enterGameScene();
    this.ui.startTutorial(() => {
      this.ui.toast('¡Tutorial completado! 🎉', { icon: '✅', duration: 1100 });
      this.returnToHub();
    });
  }

  _setTutorialRack() {
    const ids = ['syll-ca', 'syll-sa', 'letter-s', 'syll-si', 'syll-ta', 'letter-i', 'letter-t', 'letter-o'];
    const tiles = ids.map((id) => DEF_BY_ID.get(id)).filter(Boolean).map((d) => createTileInstance(d));
    this.game.players[0].rack.tiles = tiles;
    this.game.beginTurn();
    this.gameScene?.sync();
  }

  confirmTurn() {
    // Comodín universal sin asignar: pedir valor antes de confirmar.
    const unassigned = this.game.board.allTiles().find(
      (t) => t.type === TILE_TYPE.WILDCARD && t.wildKind === 'universal' && !t.assignedText
    );
    if (unassigned) {
      this.ui.openWildcardAssign(unassigned, (val) => this.commit('assignWildcard', unassigned.instanceId, val));
      return;
    }

    // Invitado (multijugador): el host valida y puntúa; aquí solo se delega la acción.
    if (this.net && !this.net.isHost) {
      this.commit('confirmTurn');
      return;
    }

    // Supervivencia: la jugada debe incluir una palabra de longitud mínima creciente.
    if (this._modeState?.survival && !this._survivalRequirementMet()) {
      this.ui.toast(`Necesitas una palabra de ${this._modeState.minLen}+ letras.`, { icon: '💀' });
      this.bus.emit(EVENTS.PLAY_SFX, { name: 'invalid' });
      return;
    }

    const res = this.commit('confirmTurn');
    if (!res || !res.ok) {
      this.ui.toast(res.reason, { icon: '⚠️' });
      this.bus.emit(EVENTS.PLAY_SFX, { name: 'invalid' });
      return;
    }
    this.audio.vibrate(30);
    // Si había palabras incorrectas, sus fichas han vuelto a la mano (las buenas se quedan).
    if (res.returnedInvalid) {
      this.ui.toast('Las palabras incorrectas han vuelto a tu mano.', { icon: '↩️', duration: 2600 });
    }
    // NO reorganizamos automáticamente: el tablero se recoloca SOLO con doble toque
    // (control del jugador). sync()/_separateWords ya evita solapes.
    this.hud().showTurnScore(res.breakdown);
    if (res.evolutions?.length) this.bus.emit(EVENTS.PLAY_SFX, { name: 'combo' });
    this._recordTurnProgress(res);

    // Bonus del puzzle diario si se construye la palabra del día.
    if (this._modeState?.daily && !this._modeState.solved) {
      const target = normalizeForLookup(this._modeState.targetWord);
      if (res.newWords.some((w) => normalizeForLookup(w) === target)) {
        this._modeState.solved = true;
        this.game.activePlayer.addScore(25);
        this.ui.toast('¡Palabra del día conseguida! +25 pts', { icon: '⭐', duration: 2600 });
        this.bus.emit(EVENTS.PLAY_SFX, { name: 'reward' });
      }
    }

    // Supervivencia: avanzar de ronda.
    if (this._modeState?.survival && res.ok) {
      this._survivalAdvance();
    }

    if (!res.ended) this._afterHumanTurn();
  }

  /** ¿La jugada actual incluye una palabra nueva de longitud >= minLen? (Supervivencia) */
  _survivalRequirementMet() {
    const prev = new Set(this.game.state.turnStart.validWords.map((w) => normalizeForLookup(w)));
    for (const word of this.game.board.words) {
      const r = this.game.validator.validateWord(word.tiles);
      if (r.valid && !prev.has(normalizeForLookup(r.word)) && letterCount(r.word) >= this._modeState.minLen) {
        return true;
      }
    }
    return false;
  }

  _survivalAdvance() {
    const s = this._modeState;
    s.round += 1;
    s.minLen = Math.min(8, s.minLen + 1);
    s.timePerRound = Math.max(20, s.timePerRound - 5);
    // Reinicia el temporizador de la nueva ronda.
    this.game.state.timer.seconds = s.timePerRound;
    this.ui.toast(`¡Ronda ${s.round}! Palabra de ${s.minLen}+ letras · ${s.timePerRound}s`, { icon: '💀', duration: 2600 });
  }

  _humanPass() {
    // Pasar = robar 1 ficha (si queda saco) y ceder el turno.
    if (this._modeState?.survival) return this._endSurvival();
    // En el puzzle diario, pasar finaliza el reto y registra la puntuación.
    if (this._modeState?.daily) { this.game.endNow(); return; }
    this.commit('drawAndPass');
    if (!this.net || this.net.isHost) this._afterHumanTurn();
  }

  _endSurvival() {
    this._stopTimer();
    SaveManager.clearGame();
    const s = this._modeState;
    const rounds = s.round - 1;
    const score = this.game.players[0].score;
    this.game.state.finished = true;
    this._modeState = null;

    const xp = this.progression.applyGameResult({ won: rounds > 0, score, longestWord: this.progression.profile.stats.longestWord });
    this.progression.setStatMax('survivalBest', rounds);
    this.progression.save();
    this.bus.emit(EVENTS.PLAY_SFX, { name: rounds > 0 ? 'victory' : 'defeat' });

    this.ui.modals.open({
      className: 'results-modal',
      dismissible: false,
      title: '💀 Supervivencia',
      bodyHTML: `<div class="result-stats">
        <div class="rstat"><span class="rs-ic">🏅</span><span>Rondas superadas</span><b>${rounds}</b></div>
        <div class="rstat"><span class="rs-ic">⭐</span><span>Puntuación</span><b>${score}</b></div>
        <div class="rstat"><span class="rs-ic">🏆</span><span>Mejor marca</span><b>${this.progression.profile.stats.survivalBest || rounds}</b></div>
        <div class="rstat"><span class="rs-ic">✨</span><span>Experiencia</span><b>+${xp} XP</b></div>
      </div>`,
      buttons: [
        { label: 'Volver', className: 'btn-ghost', onClick: () => this.returnToHub() },
        { label: 'Reintentar', className: 'btn-primary', onClick: () => this.startGame({ mode: GAME_MODE.SURVIVAL }) },
      ],
    });
  }

  _afterHumanTurn() {
    this._stopTimer();
    // Asíncrono: al acabar TU turno se sube el estado y sale (el rival juega cuando pueda).
    if (this.currentMode === GAME_MODE.ASYNC) { this._asyncFinishTurn(); return; }
    this._maybeRunAI(); // encadena IA o reinicia el temporizador del humano
  }

  /** Si el jugador activo es IA, ejecuta su turno; si es el humano, arranca su temporizador. */
  _maybeRunAI() {
    if (!this.game.state || this.game.state.finished) return;
    if (!this.game.activePlayer.isAI) {
      // Turno del humano: sin "pensando" y arranca su cuenta atrás de 60 s.
      this.ui.hud.setThinking(null);
      this._startTimerIfNeeded();
      return;
    }
    const level = this.game.activePlayer.aiLevel || 'normal';
    const activeName = this.game.activePlayer.name;
    // Icono del toast = la IMAGEN (cara) del jugador, no un emoji.
    const activeAvatar = avatarHTML(this.game.activePlayer);
    const activeId = this.game.activePlayer.id;

    // La IA "piensa": indicador visible + cuenta atrás en SU ficha, y tarda entre
    // 12 y 45 s (aleatorio) en jugar, como un rival humano.
    this.ui.hud.setThinking(activeId);
    const thinkMs = 6000 + Math.floor(Math.random() * 2001); // 6–8 s
    this._startAiTimer();

    // Guardamos el timeout para poder CANCELARLO al abandonar, y capturamos el id de sesión:
    // si la partida cambia (abandono/nueva), esta IA no debe tocar nada (evita que una IA de una
    // partida abandonada siga poniendo palabras en la nueva).
    const session = this._gameSession;
    this._aiTimeout = setTimeout(async () => {
      this._aiTimeout = null;
      this._stopTimer();
      if (!this._sessionAlive(session)) { this.ui.hud.setThinking(null); return; }
      // La búsqueda de palabra nueva se delega al Web Worker (no bloquea el render).
      const ai = new AIPlayer(this.game, level, {
        search: (rack, dict, opts) => this.aiRunner.findBestWord(rack, dict, opts),
      });
      const result = await ai.playTurn();
      // El "await" pudo resolverse DESPUÉS de abandonar: revalida la sesión antes de tocar nada.
      if (!this._sessionAlive(session)) { this.ui.hud.setThinking(null); return; }
      this.ui.hud.setThinking(null);
      this.gameScene?.sync(); // coloca la palabra nueva sin solapes; NO reorganiza todo el tablero
      if (result.action === 'word' || result.action === 'evolve') {
        const w = (result.detail?.word || result.detail?.result || '').toUpperCase();
        this.ui.toast(`${activeName} jugó ${w}`, { icon: activeAvatar, duration: 1600 });
      }
      // Continúa la cadena si el siguiente también es IA.
      this._maybeRunAI();
    }, thinkMs);
  }

  /** ¿Sigue viva ESTA partida? (misma sesión, con estado y sin terminar). */
  _sessionAlive(session) {
    return session === this._gameSession && !!this.game.state && !this.game.state.finished;
  }

  /** Cancela el "pensar" pendiente de la IA (si lo hay). */
  _cancelAI() {
    if (this._aiTimeout) { clearTimeout(this._aiTimeout); this._aiTimeout = null; }
  }

  /** Cancela y oculta el cartel de "Buscando rivales…" y restablece las tarjetas de jugador. */
  _cancelSearching() {
    if (this._searchTimeout) { clearTimeout(this._searchTimeout); this._searchTimeout = null; }
    if (this._searchReveals) { this._searchReveals.forEach((t) => clearTimeout(t)); this._searchReveals = null; }
    if (this._searchEl) { this._searchEl.remove(); this._searchEl = null; }
    const hudEl = this.ui.hud?.el;
    if (hudEl) {
      hudEl.classList.remove('matchmaking');
      hudEl.querySelectorAll('.player-card').forEach((c) => c.classList.remove('mm-hidden', 'mm-in'));
    }
  }

  /** Muestra "BUSCANDO RIVALES…" con spinner sobre el tablero durante 8–15 s (aleatorio).
   *  Los rivales NO aparecen de golpe: sus tarjetas se van dibujando escalonadamente según
   *  "se encuentran". Cancelable al abandonar (sesión). */
  _showSearching(session, onDone) {
    this._cancelSearching();
    const hudEl = this.ui.hud?.el;
    const cards = hudEl ? [...hudEl.querySelectorAll('.player-card')] : [];
    const rivals = cards.slice(1); // índice 0 = tú (humano); el resto, rivales IA
    const total = rivals.length;
    if (hudEl) hudEl.classList.add('matchmaking');
    rivals.forEach((c) => c.classList.add('mm-hidden')); // ocúltalos hasta encontrarlos

    const el = document.createElement('div');
    el.className = 'searching-overlay';
    el.innerHTML = `
      <div class="so-box">
        <div class="so-spinner" aria-hidden="true"></div>
        <div class="so-text">BUSCANDO RIVALES</div>
        <div class="so-sub"><span id="so-count">0</span>/${total} rivales encontrados</div>
      </div>`;
    (this.ui.root || document.body).appendChild(el);
    this._searchEl = el;
    const countEl = el.querySelector('#so-count');

    const ms = 8000 + Math.floor(Math.random() * 7001); // 8–15 s
    // Reparte la aparición de cada rival dentro de la ventana de búsqueda (con algo de azar).
    this._searchReveals = [];
    let found = 0;
    const startAt = 1200;
    const endAt = Math.max(startAt + 500, ms - 900);
    rivals.forEach((card, i) => {
      const frac = total <= 1 ? 0.5 : i / (total - 1);
      const jitter = 0.75 + Math.random() * 0.25;
      const at = Math.floor(startAt + (endAt - startAt) * frac * jitter);
      const to = setTimeout(() => {
        if (!this._sessionAlive(session)) return;
        card.classList.remove('mm-hidden');
        card.classList.add('mm-in');
        found += 1;
        if (countEl) countEl.textContent = String(found);
      }, at);
      this._searchReveals.push(to);
    });

    this._searchTimeout = setTimeout(() => {
      this._searchTimeout = null;
      rivals.forEach((c) => c.classList.remove('mm-hidden', 'mm-in'));
      if (hudEl) hudEl.classList.remove('matchmaking');
      if (this._searchEl) { this._searchEl.remove(); this._searchEl = null; }
      if (!this._sessionAlive(session)) return;
      onDone();
    }, ms);
  }

  /** Reinicia TODO lo que corre en segundo plano de una partida: nueva sesión (invalida IAs
   *  y timeouts anteriores), cancela el "pensar" de la IA y para las cuentas atrás. */
  _resetGameRuntime() {
    this._gameSession += 1;
    this._cancelAI();
    this._cancelSearching();
    this._stopTimer();
    this.ui.hud.setThinking(null);
  }

  /** Cuenta atrás mostrada en la ficha de la IA mientras "piensa" (no auto-pasa). */
  _startAiTimer() {
    if (this.net) return;
    this._stopTimer();
    if (!this.game.state.timer?.enabled) return;
    let remaining = this.game.state.timer.seconds;
    this.hud().setTimer(remaining, true);
    this._timerHandle = setInterval(() => {
      remaining -= 1;
      this.hud().setTimer(Math.max(0, remaining), true);
      if (remaining <= 0) this._stopTimer();
    }, 1000);
  }

  // ---------------------------------------------------------------------------
  // Temporizador de turno (máximo 60 s en todos los modos)
  // ---------------------------------------------------------------------------
  /** Cuenta atrás del multijugador: reinicia 60 s para el turno activo. Solo MI turno
   *  auto-pasa al agotarse; en el turno del rival es solo indicativa (él lo gestiona). */
  _updateOnlineTimer() {
    if (this.currentMode !== GAME_MODE.ONLINE) return;
    this._stopTimer();
    const st = this.game.state;
    if (!st || st.finished || !st.timer?.enabled) return;
    let remaining = st.timer.seconds;
    this.hud().setTimer(remaining, true);
    this._timerHandle = setInterval(() => {
      remaining -= 1;
      this.hud().setTimer(Math.max(0, remaining), true);
      if (remaining <= 0) {
        this._stopTimer();
        if (this.game.isSelfTurn()) this._onlineTimeout();
      }
    }, 1000);
  }

  _onlineTimeout() {
    this.ui.toast('¡Tiempo!', { icon: '⏱️' });
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'invalid' });
    // Igual que al confirmar: las palabras CORRECTAS se quedan (y puntúan) y las fichas de
    // las INCORRECTAS/sueltas vuelven a la mano. El host lo aplica y sincroniza. (Antes hacía
    // drawAndPass, que revertía TODO el turno.)
    this.commit('resolveTimeout');
  }

  _startTimerIfNeeded() {
    if (this.net) return; // el multijugador usa _updateOnlineTimer
    if (this._timerHandle) return; // ya hay una cuenta atrás en marcha
    if (!this.game.state.timer.enabled) return;
    if (this.game.state.finished || this.game.activePlayer.isAI) return;
    let remaining = this.game.state.timer.seconds;
    this.hud().setTimer(remaining, true);
    this._timerHandle = setInterval(() => {
      remaining -= 1;
      this.hud().setTimer(remaining, true);
      // Últimos 10 s: aviso visual (parpadeo rojo) + tic de sonido cada segundo.
      if (remaining <= 10 && remaining > 0) {
        this.bus.emit(EVENTS.PLAY_SFX, { name: 'tick' });
        this.bus.emit(EVENTS.VIBRATE, { ms: 12 });
      }
      if (remaining <= 0) {
        this._onTurnTimeout();
      }
    }, 1000);
  }

  /** Tiempo agotado: comportamiento según el modo. */
  _onTurnTimeout() {
    this._stopTimer();
    this.bus.emit(EVENTS.PLAY_SFX, { name: 'invalid' });
    if (this._modeState?.survival) return this._endSurvival();
    if (this._modeState?.daily) {
      // El reto diario no termina por tiempo: se pierde el turno y se sigue.
      this.game.resetTurn();
      this.ui.toast('¡Tiempo!', { icon: '⏱️' });
      this._afterHumanTurn();
      return;
    }
    // Resolución amable: puntúa las palabras válidas puestas, devuelve las fichas sueltas
    // a la mano y, si no hubo palabras, añade 1 ficha a la mano.
    const res = this.game.resolveTimeout();
    if (res.played && res.breakdown) {
      this.ui.toast(`¡Tiempo! Puntúas +${res.breakdown.total}`, { icon: '⏱️', duration: 2200 });
      this.hud().showTurnScore(res.breakdown);
      this._recordTurnProgress(res);
    } else {
      this.ui.toast('¡Tiempo! Sin palabras: robas 1 ficha', { icon: '⏱️', duration: 2200 });
    }
    if (!res.ended) this._afterHumanTurn();
  }
  _stopTimer() {
    if (this._timerHandle) clearInterval(this._timerHandle);
    this._timerHandle = null;
    this.hud().setTimer(0, false);
  }

  // ---------------------------------------------------------------------------
  // Fin de partida y progresión
  // ---------------------------------------------------------------------------
  _bindGlobalEvents() {
    this.bus.on(EVENTS.GAME_END, (summary) => this._onGameEnd(summary));
    // Multijugador: al cambiar de turno (en el host) reinicia la cuenta atrás de 60 s.
    this.bus.on(EVENTS.TURN_START, () => { if (this.currentMode === GAME_MODE.ONLINE) this._updateOnlineTimer(); });
    // Invitación directa de un amigo.
    this.bus.on(EVENTS.FRIEND_INVITE, ({ room, from }) => this._onFriendInvite(room, from));
    // Desconexión durante una partida en red: avisa (claro si el rival abandonó) y vuelve al hub.
    this.bus.on(EVENTS.NET_CLOSED, (d) => {
      if (!this.net) return;
      if (this.game.state?.finished) { this.net = null; return; } // fin normal: el rival cerró tras terminar
      const left = d?.reason === 'opponent-left' || d?.reason === 'host-left';
      if (left) {
        this.ui.toast(`${d?.name || 'Tu rival'} ha abandonado la partida.`, { icon: '🏳️', duration: 3800 });
      } else {
        this.ui.toast('Se perdió la conexión con la sala.', { icon: '🔌', duration: 3000 });
      }
      this.returnToHub();
    });
    // El invitado se desconecta de golpe (cierra la app) durante la partida: avisa al anfitrión.
    this.bus.on(EVENTS.NET_PEER_LEFT, () => {
      if (this.net && this.currentMode === GAME_MODE.ONLINE && !this.game.state?.finished) {
        this.ui.toast('Tu rival ha abandonado la partida.', { icon: '🏳️', duration: 3800 });
        this.returnToHub();
      }
    });
    this.bus.on(EVENTS.ACHIEVEMENT_UNLOCKED, ({ achievement }) => {
      // Aviso breve y semitransparente (no interrumpe el juego).
      this.ui.toast(`¡Logro! ${achievement.label}`, { icon: '🏅', duration: 1400, dim: true });
      this.bus.emit(EVENTS.PLAY_SFX, { name: 'reward' });
      this.ui.hub?.refresh?.(); // actualiza el pin del menú
    });
    this.bus.on(EVENTS.FAMILY_COMPLETED, ({ family, playerId }) => {
      this.ui.toast(`¡Familia completada: ${family.label}!`, { icon: '🧬', duration: 2600 });
      // Solo cuentan las familias completadas por el jugador LOCAL (para sus logros).
      if (playerId === this.game.selfPlayer?.id) this.progression.incStat('familiesCompleted');
    });
    // Autoguardado tras cada turno confirmado.
    this.bus.on(EVENTS.TURN_CONFIRMED, () => this._autosave());

    // Reloj GRANDE central (últimos 10 s): lo pinta SceneManager DENTRO de la escena 3D,
    // por detrás de las fichas del tablero. La lógica (≤10 s y turno propio) viene del HUD.
    this.bus.on('hud:bigtimer', ({ show, secs, ratio }) => this.sceneManager.updateBigTimer(secs, ratio, show));

    // --- Comodines de acción ---
    this.bus.on('scene:wildcard', ({ tile }) => this._onWildcardActivated(tile));
    this.bus.on('scene:wildTransform', ({ target, wildId, options }) => {
      this.ui.openTransformChooser(options, {
        onChoose: (val) => {
          this.commit('transformTile', target.instanceId, val, wildId);
          this.gameScene?.endWildTargeting();
          this.bus.emit(EVENTS.PLAY_SFX, { name: 'snap' });
        },
        onCancel: () => this.gameScene?.endWildTargeting(),
      });
    });
    this.bus.on('scene:wildResult', (res) => {
      if (res.ok) {
        this.bus.emit(EVENTS.PLAY_SFX, { name: res.kind === 'merge' ? 'combo' : 'snap' });
        this.audio.vibrate(20);
      } else if (res.reason) {
        this.ui.toast(res.reason, { icon: '⚠️' });
      }
    });
  }

  _onWildcardActivated(tile) {
    if (tile.wildKind === 'universal') {
      this.ui.openWildcardAssign(tile, (val) => this.commit('assignWildcard', tile.instanceId, val));
      return;
    }
    // Comodín de herramienta: entra en modo de selección de objetivo.
    this.gameScene?.beginWildTargeting(tile);
    const hints = {
      split: 'Toca una ficha de la mesa para dividirla en letras.',
      merge: 'Toca dos fichas contiguas para fusionarlas.',
      transform: 'Toca una ficha de la mesa para transformarla.',
    };
    this.ui.toast(hints[tile.wildKind] || 'Selecciona un objetivo.', { icon: '✨', duration: 1600, center: true });
  }

  _recordTurnProgress(res) {
    const p = this.progression;
    p.incStat('evolutions', res.evolutions.length);
    if (res.newWords.length >= 3) p.incStat('tripleTurns');
    this._recordSelfWords(res.newWords);
  }

  /** Registra en el perfil las palabras creadas por el jugador LOCAL (nº, más larga, mejor). */
  _recordSelfWords(newWords) {
    if (!newWords || !newWords.length) return;
    const p = this.progression;
    p.incStat('wordsCreated', newWords.length);
    p.addDiscoveredWords(newWords);
    for (const w of newWords) {
      p.recordLongestWord(w);
      p.recordBestWord(w, this._wordValue(w));
    }
    p.save();
    this.hub()?.refresh?.();
  }

  /** Valor de una palabra = suma del valor de sus fichas en la mesa (respaldo: nº de letras). */
  _wordValue(text) {
    const norm = normalizeForLookup(text);
    const board = this.game.board;
    const w = board?.words?.find((bw) => normalizeForLookup(board.wordText(bw)) === norm);
    if (w) return w.tiles.reduce((s, t) => s + (t.value || 0), 0);
    return letterCount(text);
  }

  _onGameEnd(summary) {
    this._stopTimer();
    SaveManager.clearGame();
    // Host: comunica el fin a los invitados.
    if (this.net?.isHost) this.net.broadcastEnd(summary);
    // Asíncrono: guarda el estado final de la partida en Firebase.
    if (this.currentMode === GAME_MODE.ASYNC) this._asyncSaveFinished();

    // Puzzle diario: registra la mejor puntuación del día.
    if (this.currentMode === GAME_MODE.DAILY) {
      const humanScore = summary.players.find((p) => p.id === 'p0')?.score || 0;
      const daily = SaveManager.loadDaily();
      const today = this._todayStr();
      if (!daily[today] || humanScore > daily[today].best) {
        daily[today] = { best: humanScore, date: today };
        SaveManager.saveDaily(daily);
      }
    }
    // Ranking (menos tutorial/supervivencia).
    if (this.currentMode !== GAME_MODE.TUTORIAL && this.currentMode !== GAME_MODE.SURVIVAL) {
      const selfId = this.game.selfPlayer?.id || 'p0';
      // Copia LOCAL (respaldo sin conexión).
      const entries = (summary.players || []).map((p) => ({ name: p.name, avatar: p.avatar, score: p.score, self: p.id === selfId }));
      this.progression.recordLeaderboard(entries, Date.now());
      // Ranking GLOBAL en Firebase: sube TU mejor puntuación SOLO si estás logueado con Google.
      // Los INVITADOS (anónimos) NO suben al ranking (se les avisa aparte).
      const self = (summary.players || []).find((p) => p.id === selfId);
      const g = this.progression.profile.google;
      if (g?.uid && !g.anon && self && typeof self.score === 'number') {
        this.ranking.submitBest({ uid: g.uid, name: this.progression.profile.name, avatar: this._selfRankAvatar(), score: self.score });
      } else if (g?.anon && self && this.currentMode !== GAME_MODE.ONLINE && !this._guestRankNoticeShown) {
        // Aviso (una vez por sesión): como invitado, la puntuación no cuenta en el ranking.
        this._guestRankNoticeShown = true;
        this.ui.toast('Juegas como invitado: tu puntuación no entra en el ranking. Inicia sesión con Google para competir.', { icon: '🏅', duration: 4200, dim: true });
      }
    }
    this._showResults(summary);
  }

  /** Muestra la pantalla de resultados (usa el jugador LOCAL como referencia). */
  _showResults(summary) {
    const selfId = this.game.selfPlayer?.id || 'p0';
    const isSelfWinner = summary.winnerId === selfId;
    const selfScore = summary.players.find((p) => p.id === selfId)?.score || 0;
    // Las palabras (nº, más larga, mejor) se registran por turno; aquí solo victoria/puntuación.
    const xpGained = this.progression.applyGameResult({ won: isSelfWinner, score: selfScore });
    this.bus.emit(EVENTS.PLAY_SFX, { name: isSelfWinner ? 'victory' : 'defeat' });
    // Feedback visual grande de VICTORIA/DERROTA (~3 s) y DESPUÉS la pantalla de resultados.
    this.ui.showEndSplash(isSelfWinner).then(() => {
      this.ui.results.show(summary, {
        xpGained,
        isHumanWinner: isSelfWinner,
        selfId,
        onRematch: this.net ? null : () => this.startGame({ mode: this.currentMode, aiLevel: this.game.state.aiLevel, numAI: this.game.players.length - 1 }),
        onHub: () => this.returnToHub(),
      });
    });
  }

  /** Abandonar partida (pausa → "Abandonar"): cuenta como abandonada si estaba en curso. */
  resignGame() {
    const inProgress = this.currentMode !== GAME_MODE.TUTORIAL
      && this.game.state && !this.game.state.finished;
    if (inProgress) {
      this.progression.incStat('abandoned');
      // Avisa al rival de que abandonas (le llegará la alerta).
      if (this.net) { try { this.net.net?.announceLeave(); } catch { /* noop */ } }
    }
    this.returnToHub();
  }

  returnToHub() {
    // Reinicia TODO al abandonar/terminar: invalida la sesión (nada de la partida anterior
    // puede seguir ejecutándose), cancela IA/timeouts y borra el guardado (no se reanuda una
    // partida abandonada). Así al entrar de nuevo se empieza limpio, sin IAs fantasma.
    this._resetGameRuntime();
    SaveManager.clearGame();
    this._modeState = null;
    if (this.net) { this.net.dispose(); this.net.net?.leave(); this.net = null; }
    this.game.state = null; // descarta el estado de la partida abandonada
    this.game.selfPlayerId = null;
    this.game.selfIsHost = null;
    this._disposeGameScene();
    this.wordDefs?.clear();
    this.sceneManager.setWatermarkVisible(false);
    this.ui.modals.closeAll();
    this.ui.showHub();
  }

  // ---------------------------------------------------------------------------
  // Utilidades
  // ---------------------------------------------------------------------------
  _disposeGameScene() {
    if (this.gameScene) {
      this.gameScene.dispose();
      this.gameScene = null;
    }
  }

  hud() {
    return this.ui.hud;
  }
  hub() {
    return this.ui.hub;
  }

  _bindShortcuts() {
    this.input.on('Enter', () => { if (this.game.state && this.game.isHumanTurn()) this.confirmTurn(); });
    this.input.on('ctrl+z', () => { if (this.game.state && this.game.isHumanTurn()) this.game.undo(); });
    this.input.on('Escape', () => {
      this.gameScene?.endWildTargeting?.();
      this.gameScene?._clearSelection();
    });
  }

  _autosave() {
    if (this.game.state && !this.game.state.finished) {
      SaveManager.saveGame(this.game.serialize());
    }
  }
  /** Aplica los ajustes de accesibilidad guardados (al iniciar y tras cambiarlos). */
  _applyAccessibility() {
    const a = this.config.accessibility || {};
    document.body.classList.toggle('high-contrast', !!a.highContrast);
    document.body.classList.toggle('reduce-motion', !!a.reduceMotion);
    if (a.textScale) document.documentElement.style.setProperty('--text-scale', String(a.textScale));
  }
  _persistSettings() {
    SaveManager.saveSettings({
      audio: this.config.audio,
      accessibility: this.config.accessibility,
      graphics: this.config.graphics,
    });
  }

  _claimDaily() {
    const r = this.progression.claimDailyReward(this._todayStr());
    if (r.already) this.ui.toast('Ya reclamaste la recompensa de hoy.', { icon: '📅' });
    else {
      this.ui.toast(`+${r.coins} 🪙 · Racha ${r.streak} 🔥`, { icon: '🎁', duration: 2600 });
      this.bus.emit(EVENTS.PLAY_SFX, { name: 'reward' });
      this.hub().refresh();
    }
  }

  /** Palabra del día (determinista por fecha). */
  _dailyWord() {
    const words = [...this.game.validator.dictionary].filter((w) => w.length >= 4 && w.length <= 7);
    const idx = seedFromDateString(this._todayStr()) % words.length;
    return words[idx];
  }

  _dailyWordHTML() {
    const word = this._dailyWord();
    const daily = SaveManager.loadDaily();
    const best = daily[this._todayStr()]?.best || 0;
    return `<p class="modal-text">La palabra del día es:</p><div class="daily-word">${word.toUpperCase()}</div>
      <p class="hint">Constrúyela en el modo Puzzle diario para un bonus. Mejor puntuación de hoy: ${best}.</p>`;
  }

  _todayStr() {
    // Fecha local YYYY-MM-DD sin depender de aleatoriedad.
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}
